import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Download, Lock } from "lucide-react";
import { api } from "@/lib/api";
import { BeatCard } from "@/components/BeatCard";
import { Reveal } from "@/components/Reveal";
import { GENRES } from "@/lib/genres";

export default function Beats() {
  const [beats, setBeats] = useState([]);
  const [genre, setGenre] = useState("All");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/beats").then((r) => setBeats(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const genreCounts = useMemo(() => {
    const counts = new Map();
    for (const b of beats) counts.set(b.genre, (counts.get(b.genre) || 0) + 1);
    return counts;
  }, [beats]);
  const genres = useMemo(() => ["All", ...GENRES], []);
  const filtered = useMemo(() => (genre === "All" ? beats : beats.filter((b) => b.genre === genre)), [beats, genre]);

  const freeBeats = useMemo(() => filtered.filter((b) => b.free_download && !b.sold_exclusive), [filtered]);
  const exclusiveBeats = useMemo(() => filtered.filter((b) => !b.free_download && ((b.prices?.exclusive ?? 0) > 0 || b.sold_exclusive)), [filtered]);
  const leaseBeats = useMemo(() => filtered.filter((b) => !b.free_download && !(b.prices?.exclusive > 0) && !b.sold_exclusive), [filtered]);

  return (
    <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 sm:py-20 lg:px-8" data-testid="beats-page">
      <Reveal>
        <p className="font-mono text-xs uppercase tracking-widest text-emerald-300">The vault</p>
        <h1 className="font-heading mt-3 text-4xl font-extrabold tracking-tight text-white sm:text-5xl">Beats Store</h1>
        <p className="mt-4 max-w-xl text-sm text-zinc-400 sm:text-base">
          Every lease includes an untagged file and a signed PDF license, delivered instantly after checkout.
          Grab a free beat, cook something up, then come back and clear it.
        </p>
      </Reveal>

      <div className="mt-8 flex flex-wrap gap-2" data-testid="genre-filters">
        {genres.map((g) => {
          const count = g === "All" ? beats.length : genreCounts.get(g) || 0;
          const empty = count === 0;
          const active = genre === g;
          return (
            <button
              key={g}
              data-testid={`genre-filter-${g.toLowerCase().replace(/\s/g, "-").replace(/&/g, "and")}`}
              onClick={() => setGenre(g)}
              aria-pressed={active}
              className={`flex items-center gap-1.5 rounded-full px-5 py-2 text-xs font-bold uppercase tracking-wider transition-all ${
                active
                  ? "bg-emerald-300 text-black"
                  : empty
                  ? "border border-white/5 text-zinc-600 hover:border-emerald-300/30 hover:text-emerald-200/70"
                  : "border border-white/10 text-zinc-400 hover:border-emerald-300/40 hover:text-emerald-200"
              }`}
            >
              {g}
              {!active && count > 0 && (
                <span className="font-mono text-[9px] font-normal lowercase tracking-normal text-emerald-300/80">
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {!loading && genre !== "All" && filtered.length === 0 && (
        <div
          className="mt-10 flex flex-col items-center justify-center gap-3 rounded-3xl border border-dashed border-white/10 bg-[#0F0F13] px-6 py-14 text-center"
          data-testid="genre-empty-state"
        >
          <p className="font-mono text-[11px] uppercase tracking-widest text-emerald-300">Coming soon</p>
          <h2 className="font-heading text-2xl font-bold text-white">No {genre} beats in the vault yet</h2>
          <p className="max-w-sm text-sm text-zinc-400">
            New {genre.toLowerCase()} drops land here as soon as they're mixed. In the meantime, scroll the rest of
            the catalog or ask the AI concierge for the closest vibe.
          </p>
          <button
            type="button"
            onClick={() => setGenre("All")}
            data-testid="genre-empty-view-all"
            className="mt-2 rounded-full border border-emerald-300/40 bg-emerald-300/5 px-5 py-2 text-xs font-bold uppercase tracking-wider text-emerald-200 transition-colors hover:border-emerald-300 hover:bg-emerald-300/15"
          >
            Browse everything
          </button>
        </div>
      )}

      {!loading && freeBeats.length > 0 && (
        <section className="mt-12" data-testid="free-beats-section">
          <Reveal>
            <div className="flex items-center gap-3">
              <Download size={16} className="text-emerald-300" />
              <p className="font-mono text-xs uppercase tracking-widest text-emerald-300">Free downloads</p>
            </div>
            <h2 className="font-heading mt-3 text-2xl font-bold tracking-tight text-white sm:text-3xl">Free Beats</h2>
            <p className="mt-2 max-w-lg text-sm text-zinc-400">
              Follow or subscribe to unlock a free tagged MP3. Cook up a demo — when the record is ready to drop,
              come back and lease or buy the exclusive rights.
            </p>
          </Reveal>
          <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="free-beats-grid">
            {freeBeats.map((b, i) => (
              <BeatCard key={b.id} beat={b} list={freeBeats} index={i} />
            ))}
          </div>
        </section>
      )}

      <section className="mt-16" data-testid="lease-beats-section">
        <Reveal>
          <h2 className="font-heading text-2xl font-bold tracking-tight text-white sm:text-3xl">Lease a Beat</h2>
          <p className="mt-2 max-w-lg text-sm text-zinc-400">
            Standard MP3 leases from $14.99, or go Premium for the WAV + stems. Instant download with your PDF license.
          </p>
        </Reveal>
        <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="beats-grid">
          {leaseBeats.map((b, i) => (
            <BeatCard key={b.id} beat={b} list={leaseBeats} index={i} />
          ))}
        </div>
        {!loading && leaseBeats.length === 0 && (
          <p className="mt-16 text-center text-sm text-zinc-600" data-testid="beats-empty">No beats in this genre yet — check back soon.</p>
        )}
      </section>

      {!loading && exclusiveBeats.length > 0 && (
        <section className="mt-16" data-testid="exclusive-beats-section">
          <Reveal>
            <div className="flex items-center gap-3">
              <Lock size={16} className="text-emerald-300" />
              <p className="font-mono text-xs uppercase tracking-widest text-emerald-300">One owner only</p>
            </div>
            <h2 className="font-heading mt-3 text-2xl font-bold tracking-tight text-white sm:text-3xl">Exclusive Rights</h2>
            <p className="mt-2 max-w-lg text-sm text-zinc-400">
              Own the beat outright — it comes off the store the moment you buy. Unlimited everything, signed exclusive agreement.
            </p>
          </Reveal>
          <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="exclusive-beats-grid">
            {exclusiveBeats.map((b, i) => (
              <BeatCard key={b.id} beat={b} list={exclusiveBeats} index={i} />
            ))}
          </div>
        </section>
      )}

      <Reveal delay={0.1}>
        <div className="mt-20 flex flex-col items-center rounded-3xl border border-dashed border-emerald-300/30 bg-emerald-300/5 p-10 text-center" data-testid="vault-teaser">
          <Lock size={28} className="text-emerald-300" />
          <p className="font-heading mt-4 text-lg font-bold text-white">Members-only beat vault — coming soon</p>
          <p className="mt-2 max-w-md text-sm text-zinc-400">
            Vault-only beats the public never hears are on the way. Create an account now so you're first in when it drops.
          </p>
          <a href="/account" data-testid="vault-teaser-cta"
            className="mt-6 flex items-center gap-2 rounded-full bg-emerald-300 px-7 py-3 text-xs font-bold uppercase tracking-wider text-black transition-colors hover:bg-emerald-200">
            Create Account <ArrowRight size={13} />
          </a>
        </div>
      </Reveal>
    </div>
  );
}
